import type { ElementSelection } from '@crowdex/plugin-contracts';
import { useEffect, useRef } from 'react';
import type { MentionTarget } from '@/lib/side-panel/mention-targets';

/**
 * What reaches the open conversation's composer: a request to send, a draft to complete, or
 * elements the user commented on (sent at once with `send`).
 */
type Request =
  | { send: string }
  | { draft: MentionTarget }
  | { insert: string }
  | { attach: ElementSelection[]; send: boolean; text?: string };
type Composer = { conversation: string; current: (request: Request) => void };

/**
 * Where `ask` (a view's `agent.ask`, an action's `run.ask`) sends its text, and where a draft
 * goes: the composer of the conversation the view belongs to (an app's own, beside others in a
 * split), else the open conversation's, the latest mounted. A plugin never names a
 * conversation. Asked while no composer is mounted, the request waits for the next one.
 */
const composers: Composer[] = [];
const waiting: Request[] = [];

function deliver(request: Request, conversation: string | undefined): void {
  const composer =
    composers.findLast((candidate) => candidate.conversation === conversation) ?? composers.at(-1);
  if (composer === undefined) waiting.push(request);
  else composer.current(request);
}

/** How long a request for a new conversation waits for it to open. */
const NEXT_CONVERSATION_MS = 15_000;

/**
 * Requests for one conversation by its id (one being opened, a new one saved for a copy): kept
 * until its own composer mounts, never delivered to another one's.
 */
const forConversation: { conversation: string; request: Request; until: number }[] = [];

function deliverTo(conversation: string, request: Request): void {
  const composer = composers.findLast((candidate) => candidate.conversation === conversation);
  if (composer !== undefined) composer.current(request);
  else forConversation.push({ conversation, request, until: Date.now() + NEXT_CONVERSATION_MS });
}

/** Sends `text` from `conversation`'s composer, now or once it mounts (it is being opened). */
export function sendInConversation(text: string, conversation: string): void {
  deliverTo(conversation, { send: text });
}

/** Puts a reference in `conversation`'s composer, focused, now or once it mounts. */
export function draftInConversation(reference: MentionTarget, conversation: string): void {
  deliverTo(conversation, { draft: reference });
}

/** Writes text in the composer of the next conversation whose composer mounts (an example). */
export function insertNextConversation(text: string): void {
  forNextConversation({ insert: text });
}

/**
 * Requests for a conversation being opened: delivered to the first composer of a conversation
 * that had none when it was asked, never to one already open.
 */
const next: { request: Request; known: ReadonlySet<string>; until: number }[] = [];

/**
 * Sends `text` from the next conversation whose composer mounts: the new conversation the caller
 * is opening, never the one open now.
 */
export function askNextConversation(text: string): void {
  forNextConversation({ send: text });
}

/**
 * Puts a reference in the composer of the next conversation whose composer mounts (the new one
 * the caller is opening), for the user to complete and send: a template to start from.
 */
export function draftNextConversation(reference: MentionTarget): void {
  forNextConversation({ draft: reference });
}

function forNextConversation(request: Request): void {
  next.push({
    request,
    known: new Set(composers.map((composer) => composer.conversation)),
    until: Date.now() + NEXT_CONVERSATION_MS,
  });
}

export function askAgent(text: string, conversation?: string): void {
  deliver({ send: text }, conversation);
}

/** Puts a reference in the composer, focused, for the user to complete and send. */
export function draftAgent(reference: MentionTarget, conversation?: string): void {
  deliver({ draft: reference }, conversation);
}

/** Writes text in the composer, focused, for the user to complete and send (a file: `@path`). */
export function insertInComposer(text: string, conversation?: string): void {
  deliver({ insert: text }, conversation);
}

/**
 * The composers of artifacts open in a panel (design D15), each about the elements pointed at in
 * its artifact's view: what that view attaches goes there, not to the conversation's composer.
 */
const artifactComposers: { artifact: string; current: (selections: ElementSelection[]) => void }[] =
  [];

/**
 * Puts elements the user commented on in the composer, as chips; `send` sends them at once. From
 * an artifact's view (`artifact`), they go to that artifact's composer while one is open.
 */
export function attachToAgent(
  selections: ElementSelection[],
  send: boolean,
  conversation?: string,
  artifact?: string,
): void {
  const own =
    artifact === undefined
      ? undefined
      : artifactComposers.findLast((candidate) => candidate.artifact === artifact);
  if (own !== undefined) own.current(selections);
  else deliver({ attach: selections, send }, conversation);
}

/** Sends `text` about the elements to the conversation's composer, as the user would send it. */
export function askAboutElements(
  text: string,
  selections: ElementSelection[],
  conversation?: string,
): void {
  if (selections.length === 0) askAgent(text, conversation);
  else deliver({ attach: selections, send: true, text }, conversation);
}

/** Registers an artifact's composer as where its view's pointed elements go, while mounted. */
export function useArtifactAttach(
  artifact: string,
  attach: (selections: ElementSelection[]) => void,
): void {
  const ref = useRef(attach);
  ref.current = attach;
  useEffect(() => {
    const entry = {
      artifact,
      current: (selections: ElementSelection[]) => ref.current(selections),
    };
    artifactComposers.push(entry);
    return () => {
      artifactComposers.splice(artifactComposers.indexOf(entry), 1);
    };
  }, [artifact]);
}

/** Registers this conversation's composer as where `ask` sends and drafts go, while mounted. */
export function useAgentAsk(
  conversation: string,
  handlers: {
    send: (text: string) => void;
    draft: (reference: MentionTarget) => void;
    attach: (selections: ElementSelection[], send: boolean, text?: string) => void;
    /** Text written in the composer (`insertInComposer`). */
    insert?: (text: string) => void;
  },
): void {
  const ref = useRef(handlers);
  ref.current = handlers;
  useEffect(() => {
    const entry: Composer = {
      conversation,
      current: (request) => {
        if ('attach' in request) {
          if (request.text === undefined) ref.current.attach(request.attach, request.send);
          else ref.current.attach(request.attach, request.send, request.text);
        } else if ('send' in request) ref.current.send(request.send);
        else if ('insert' in request) ref.current.insert?.(request.insert);
        else ref.current.draft(request.draft);
      },
    };
    composers.push(entry);
    for (const request of waiting.splice(0)) entry.current(request);
    const now = Date.now();
    // What was asked of this conversation by its id, in order; what expired goes.
    const pendingHere = forConversation.filter((pending) => pending.conversation === conversation);
    const kept = forConversation.filter(
      (pending) => pending.conversation !== conversation && pending.until >= now,
    );
    forConversation.splice(0, forConversation.length, ...kept);
    for (const pending of pendingHere) if (pending.until >= now) entry.current(pending.request);
    for (let index = next.length - 1; index >= 0; index -= 1) {
      const pending = next[index];
      if (pending === undefined) continue;
      if (pending.until < now) next.splice(index, 1);
      else if (!pending.known.has(conversation)) {
        next.splice(index, 1);
        entry.current(pending.request);
      }
    }
    return () => {
      composers.splice(composers.indexOf(entry), 1);
    };
  }, [conversation]);
}
